import React, { useState, useEffect, useCallback } from 'react';
import { X, CreditCard, ArrowLeft, RefreshCw, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { trackSubscription, trackError } from '../Utils/analytics';
import { authenticatedFetch } from '../Utils/apiHelpers';
import {
  PAYPAL_PLANS_URL,
  PAYPAL_CREATE_ORDER_URL,
  PAYPAL_SWITCH_PLAN_URL,
  PAYPAL_CURRENT_SUBSCRIPTION_URL,
  PAYPAL_CLIENT_CONFIG_URL,
} from '../../config/api';
import '../PaymentModal/PaymentModal.css';

interface PaymentModalProps {
  onClose: () => void;
  onSuccess?: () => void;
  selectedPlan?: string;
}

interface ApiPlan {
  plan_type: string;
  name: string;
  charged_amount: number;
  charged_currency: string;
  display_amount: number;
  display_currency: string;
  duration_days: number;
}

interface PlanPricingEntry {
  amount: string;
  currency: string;
  interval: string;
  planType: string;
}

interface PayPalClientConfig {
  client_id: string;
  mode: string;
  currency: string;
  intent: string;
  buyer_country: string;
  card_fields_enabled: boolean;
  apple_pay_enabled: boolean;
  components: string[];
  regional_methods: string;
}

interface CurrentSubscription {
  active: boolean;
  plan_type: string;
  status: string;
  starts_at: string;
  expires_at: string;
}

const FALLBACK_PLAN_PRICING: Record<string, PlanPricingEntry> = {
  weekly: { amount: '3.99', currency: 'GBP', interval: 'week', planType: 'WEEKLY' },
  monthly: { amount: '4.99', currency: 'GBP', interval: 'month', planType: 'MONTHLY' },
  yearly: { amount: '49.99', currency: 'GBP', interval: 'year', planType: 'YEARLY' }
};

const FALLBACK_CLIENT_CONFIG: PayPalClientConfig = {
  client_id: '',
  mode: 'sandbox',
  currency: 'GBP',
  intent: 'capture',
  buyer_country: '',
  card_fields_enabled: false,
  apple_pay_enabled: false,
  components: ['buttons'],
  regional_methods: '',
};

const PLAN_RANK: Record<string, number> = { WEEKLY: 1, MONTHLY: 2, YEARLY: 3 };

const mapApiPlansToPricing = (plans: ApiPlan[]): Record<string, PlanPricingEntry> => {
  const intervalMap: Record<number, string> = { 7: 'week', 30: 'month', 365: 'year' };
  const result: Record<string, PlanPricingEntry> = {};
  for (const plan of plans) {
    const id = plan.plan_type.toLowerCase();
    const interval = intervalMap[plan.duration_days] || id;
    result[id] = {
      amount: plan.display_amount.toString(),
      currency: plan.display_currency,
      interval,
      planType: plan.plan_type
    };
  }
  return result;
};

const PaymentModal: React.FC<PaymentModalProps> = ({ onClose, selectedPlan }) => {
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showBankTransfer, setShowBankTransfer] = useState(false);
  const [paypalOrderData, setPaypalOrderData] = useState<any>(null);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [switchDirection, setSwitchDirection] = useState<'upgrade' | 'downgrade' | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'select' | 'paypal' | 'bank'>('select');
  const [planPricing, setPlanPricing] = useState<Record<string, PlanPricingEntry>>(FALLBACK_PLAN_PRICING);
  const [clientConfig, setClientConfig] = useState<PayPalClientConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(false);
  const navigate = useNavigate();

  const currentPlan = selectedPlan && planPricing[selectedPlan]
    ? planPricing[selectedPlan]
    : planPricing.monthly;

  const hasActiveSubscription = currentSubscription?.active === true;

  const fetchClientConfig = useCallback(async () => {
    setConfigLoading(true);
    setConfigError(false);
    try {
      const response = await authenticatedFetch(PAYPAL_CLIENT_CONFIG_URL, {
        method: 'GET',
        headers: { Accept: '*/*' },
      });
      if (response.ok) {
        const data = await response.json();
        setClientConfig(data as PayPalClientConfig);
      } else {
        setClientConfig(FALLBACK_CLIENT_CONFIG);
      }
    } catch {
      setClientConfig(FALLBACK_CLIENT_CONFIG);
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClientConfig();
  }, [fetchClientConfig]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await authenticatedFetch(PAYPAL_PLANS_URL, {
          method: 'GET',
          headers: { Accept: '*/*' },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.plans && Array.isArray(data.plans) && data.plans.length > 0) {
            setPlanPricing(mapApiPlansToPricing(data.plans));
          }
        }
      } catch {
        // Keep fallback pricing on error
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    const checkActiveSubscription = async () => {
      try {
        const response = await authenticatedFetch(PAYPAL_CURRENT_SUBSCRIPTION_URL, {
          method: 'GET',
          headers: { Accept: '*/*' },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.active === true || data.status === 'active' || data.status === 'COMPLETED') {
            setCurrentSubscription(data as CurrentSubscription);
          }
        }
      } catch {
        trackError('subscription_check_failed', 'payment_modal');
      }
    };
    checkActiveSubscription();
  }, []);

  // Determine switch direction when user has an active subscription and picks a different plan
  useEffect(() => {
    if (currentSubscription && selectedPlan) {
      const currentRank = PLAN_RANK[currentSubscription.plan_type] || 0;
      const newRank = PLAN_RANK[currentPlan.planType] || 0;
      if (newRank > currentRank) setSwitchDirection('upgrade');
      else if (newRank < currentRank) setSwitchDirection('downgrade');
      else setSwitchDirection(null);
    } else {
      setSwitchDirection(null);
    }
  }, [currentSubscription, selectedPlan, currentPlan.planType]);

  const storeOrderData = (data: any) => {
    localStorage.setItem('currentOrderPlan', selectedPlan || 'monthly');
    localStorage.setItem('currentOrderId', data.order_id);
    localStorage.setItem('currentOrderAmount', data.display_amount?.toString() || currentPlan.amount);
    localStorage.setItem('currentOrderCurrency', data.display_currency || currentPlan.currency);
    localStorage.setItem('currentChargedAmount', data.charged_amount?.toString() || currentPlan.amount);
    localStorage.setItem('currentChargedCurrency', data.charged_currency || currentPlan.currency);
    localStorage.setItem('currentOrderStatus', data.status || 'CREATED');
    localStorage.setItem('currentUserId', data.user_id?.toString() || '');
    if (data.plan_switch) {
      localStorage.setItem('currentOrderIsSwitch', 'true');
    }
  };

  const redirectToApproval = (data: any) => {
    const approvalUrl = data.approval_link || data.approval_url;
    if (typeof approvalUrl !== 'string' || !approvalUrl.startsWith('https://www.paypal.com/')) {
      setError('PayPal did not return a valid approval link. Please try again later.');
      trackError('paypal_missing_approval_link', 'payment_modal');
      return false;
    }
    setPaypalOrderData(data);
    storeOrderData(data);
    window.location.href = approvalUrl;
    return true;
  };

  const handleApiError = (response: Response, data: any) => {
    if (response.status === 400) {
      if (data.message && data.message.toLowerCase().includes('active subscription')) {
        setError('You already have an active subscription. Use the plan switch option to change your plan.');
        trackError('duplicate_subscription_api', 'payment_modal');
      } else {
        setError(data.message || 'Invalid plan type or subscription already exists.');
        trackError('paypal_order_validation', 'payment_modal');
      }
    } else if (response.status === 401) {
      setError('Authentication failed. Please log in again.');
      trackError('paypal_order_auth_failed', 'payment_modal');
    } else if (response.status === 404) {
      setError('Payment service not found. Please try again later.');
      trackError('paypal_order_not_found', 'payment_modal');
    } else if (response.status === 409) {
      setError('You already have a pending order. Please complete or cancel it before selecting a new plan.');
      trackError('paypal_order_conflict', 'payment_modal');
    } else if (response.status === 500) {
      setError('Server error occurred. Please try again later or contact support.');
      trackError('paypal_order_server_error', 'payment_modal');
    } else {
      setError(data.message || 'Failed to create PayPal order. Please try again.');
      trackError('paypal_order_unknown_error', 'payment_modal');
    }
  };

  const createPayPalOrder = async () => {
    setIsProcessing(true);
    setError('');

    try {
      trackSubscription('payment_started', selectedPlan || 'unknown');

      const body = JSON.stringify({ plan_type: currentPlan.planType });
      const headers = { Accept: '*/*', 'Content-Type': 'application/json' };

      let response: Response;
      let data: any;

      if (hasActiveSubscription) {
        // Use switch-plan for existing subscribers
        trackSubscription('plan_switch_started', selectedPlan || 'unknown');
        response = await authenticatedFetch(PAYPAL_SWITCH_PLAN_URL, {
          method: 'POST',
          headers,
          body,
        });
      } else {
        response = await authenticatedFetch(PAYPAL_CREATE_ORDER_URL, {
          method: 'POST',
          headers,
          body,
        });
      }

      data = await response.json();

      if (response.ok) {
        redirectToApproval(data);
      } else {
        handleApiError(response, data);
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
      trackError('paypal_order_network_error', 'payment_modal');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToOptions = () => {
    setPaymentMethod('select');
    setError('');
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const cancelled = urlParams.get('cancelled');
    if (cancelled === 'true') {
      navigate('/payment-cancelled?reason=user_cancelled');
    } else if (token) {
      navigate(`/payment-success?token=${token}`);
    }
  }, [navigate]);

  const handleBankTransferClick = () => {
    setShowBankTransfer(true);
  };

  const handleBackToPayPal = () => {
    setShowBankTransfer(false);
  };

  const handleViewBankDetails = () => {
    const timestamp = Date.now().toString().slice(-8);
    const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const planCode = selectedPlan ? selectedPlan.charAt(0).toUpperCase() : 'M';
    const bankReference = `HC${planCode}${timestamp}${randomNum}`;
    localStorage.setItem('currentOrderReference', bankReference);
    localStorage.setItem('currentOrderPlan', selectedPlan || 'monthly');
    localStorage.setItem('currentOrderAmount', currentPlan.amount);
    onClose();
    navigate('/payment-methods');
  };

  const handleRetryConfig = () => {
    setError('');
    fetchClientConfig();
  };

  const displayCurrency = clientConfig?.currency || currentPlan.currency;
  const currencySymbol = displayCurrency === 'GBP' ? '£' : displayCurrency === 'EUR' ? '€' : '$';
  const isSandbox = clientConfig?.mode === 'sandbox';
  const buttonsEnabled = clientConfig?.components?.includes('buttons') ?? true;

  const selectedPlanLabel = selectedPlan
    ? selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)
    : 'Unknown';

  return (
    <div className="payment-overlay">
      <div className="payment-modal">
        <button className="close-button9" onClick={onClose} disabled={isProcessing}>
          <X size={24} />
        </button>

        <h2 className="payment-title">
          {hasActiveSubscription ? 'Switch Your Plan' : 'Complete Payment'}
        </h2>

        {isSandbox && clientConfig && (
          <div className="sandbox-badge">
            <AlertCircle size={14} />
            Sandbox Mode — test payments only
          </div>
        )}

        <div className="plan-summary">
          <h3>
            Plan: {selectedPlanLabel} Subscription
          </h3>
          <p className="plan-price">
            {paypalOrderData ? (
              <>
                {paypalOrderData.display_currency} {paypalOrderData.display_amount}/{currentPlan.interval}
                {paypalOrderData.charged_currency !== paypalOrderData.display_currency && (
                  <span className="charged-amount">
                    (Charged: {paypalOrderData.charged_currency} {paypalOrderData.charged_amount})
                  </span>
                )}
              </>
            ) : (
              `${currencySymbol}${currentPlan.amount}/${currentPlan.interval}`
            )}
          </p>
          {paypalOrderData && (
            <div className="order-id-display">
              <span className="order-label">Order ID:</span>
              <span className="order-number">{paypalOrderData.order_id}</span>
            </div>
          )}
        </div>

        {hasActiveSubscription && (
          <div className="active-subscription-notice" style={{ marginBottom: '16px' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {switchDirection === 'upgrade' ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
              {switchDirection === 'upgrade' ? 'Plan Upgrade' : switchDirection === 'downgrade' ? 'Plan Downgrade' : 'Same Plan'}
            </h3>
            <p>
              You currently have an active <strong>{currentSubscription?.plan_type?.toLowerCase()}</strong> subscription
              {currentSubscription?.expires_at && (
                <> valid until {new Date(currentSubscription.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</>
              )}.
              {switchDirection === 'upgrade' && ' Selecting a higher plan will upgrade your subscription immediately after payment.'}
              {switchDirection === 'downgrade' && ' Selecting a lower plan will downgrade your subscription immediately after payment.'}
              {switchDirection === null && ' This is the same plan you currently have.'}
            </p>
          </div>
        )}

        {configLoading ? (
          <div className="payment-config-loading">
            <div className="payment-config-spinner" />
            <p>Preparing payment options...</p>
          </div>
        ) : configError ? (
          <div className="payment-config-error">
            <AlertCircle size={32} />
            <p>Could not load payment configuration.</p>
            <button onClick={handleRetryConfig} className="retry-config-btn">
              <RefreshCw size={16} />
              Try Again
            </button>
          </div>
        ) : (
          <>
            {paymentMethod === 'select' && !showBankTransfer ? (
              <>
                {buttonsEnabled && (
                  <div className="paypal-section">
                    <button
                      onClick={createPayPalOrder}
                      disabled={isProcessing || (hasActiveSubscription && switchDirection === null)}
                      className="paypal-button"
                    >
                      {isProcessing
                        ? (hasActiveSubscription ? 'Switching Plan...' : 'Creating Order...')
                        : (hasActiveSubscription ? 'Switch Plan with PayPal' : 'Pay with PayPal')}
                    </button>

                    {isProcessing && (
                      <div className="processing-message">
                        {paypalOrderData ? 'Redirecting to PayPal...' : (hasActiveSubscription ? 'Creating your plan switch...' : 'Creating your order...')}
                      </div>
                    )}

                    {hasActiveSubscription && switchDirection === null && (
                      <div className="processing-message" style={{ color: '#e67e22' }}>
                        You are already on this plan. Select a different plan to switch.
                      </div>
                    )}
                  </div>
                )}

                {buttonsEnabled && (
                  <>
                    <div className="payment-divider">
                      <span>or</span>
                    </div>

                    <button
                      className="bank-transfer-btn"
                      onClick={handleBankTransferClick}
                      disabled={isProcessing}
                    >
                      <CreditCard size={20} />
                      Secure Bank Transfer
                    </button>
                  </>
                )}
              </>
            ) : paymentMethod === 'paypal' ? (
              <div>
                <button onClick={handleBackToOptions} className="back-button">
                  <ArrowLeft size={16} />
                  Back to Payment Options
                </button>
              </div>
            ) : (
              <div className="bank-transfer-section">
                <div className="bank-transfer-header">
                  <button className="back-to-paypal" onClick={handleBackToPayPal}>
                    ← Back to PayPal
                  </button>
                  <h3>Secure Bank Transfer</h3>
                </div>

                <div className="bank-transfer-info">
                  <p>Complete your payment securely via bank transfer. You'll receive a reference number for your transfer on the next page.</p>

                  <button
                    className="view-details-btn"
                    onClick={handleViewBankDetails}
                  >
                    <CreditCard size={20} />
                    View Bank Transfer Details
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="payment-security">
          <p>Your payment information is secure and encrypted</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
